import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useCart } from '@/lib/cart';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ShieldCheck, CreditCard, Wallet, Smartphone, MapPin, UtensilsCrossed, Truck, ShoppingBag, Plus, Home, Briefcase, MapPinned, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { fmtMoney } from '@/lib/realtime';
import { usePolicy } from '@/lib/policy';
import { PublicHeader, Footer } from '@/components/layout/PublicHeader';

export default function CheckoutPage() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const cancelled = sp.get('cancelled');
  const resumeOrderId = sp.get('order_id');

  const [branches, setBranches] = useState([]);
  const [tables, setTables] = useState([]);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [orderType, setOrderType] = useState('delivery');
  const [tableId, setTableId] = useState('');
  const [tableQrToken, setTableQrToken] = useState(localStorage.getItem('cg_table_qr_token') || '');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [submitting, setSubmitting] = useState(false);
  const [customerName, setCustomerName] = useState(user?.full_name || '');
  const [customerPhone, setCustomerPhone] = useState(user?.phone || '');
  const [address, setAddress] = useState({ line1: '', line2: '', city: '', state: '', postal_code: '', notes: '' });
  // Track which saved address (if any) the user picked. '__new__' means
  // they are typing a fresh address that we'll optionally save for next time.
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [saveAddressToAccount, setSaveAddressToAccount] = useState(false);
  const [newAddressLabel, setNewAddressLabel] = useState('Home');
  const [tip, setTip] = useState(0);

  useEffect(() => {
    api.get('/branches').then((r) => {
      const list = r.data || [];
      setBranches(list);
      if (list[0]) api.get(`/branches/${list[0].id}/tables`).then((tr) => setTables(tr.data || []));
    });
    if (user) {
      api.get('/me/addresses').then((r) => {
        const list = r.data || [];
        setSavedAddresses(list);
        // Preselect the first saved address by default for speed; if none,
        // default the picker to the inline "new address" mode.
        if (list.length > 0) {
          setSelectedAddressId(list[0].id);
          setAddress({
            line1: list[0].line1 || '',
            line2: list[0].line2 || '',
            city: list[0].city || '',
            state: list[0].state || '',
            postal_code: list[0].postal_code || '',
            notes: list[0].notes || '',
          });
        } else {
          setSelectedAddressId('__new__');
        }
      }).catch(() => { setSelectedAddressId('__new__'); });
    } else {
      setSelectedAddressId('__new__');
    }
  }, [user]);

  useEffect(() => {
    if (tableQrToken && !tableId) {
      setOrderType('dine_in');
    }
  }, [tableQrToken, tableId]);

  const branch = branches[0];
  const { policy } = usePolicy();
  const taxRate = policy.tax_enabled ? Number(policy.tax_rate || 0) : 0;

  // ---- Coupon ----
  const [couponCode, setCouponCode] = useState('');
  const [coupon, setCoupon] = useState(null);     // { code, type, value, discount, free_delivery_applied }
  const [couponBusy, setCouponBusy] = useState(false);
  const [couponError, setCouponError] = useState('');

  const applyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();
    if (!code) return;
    setCouponBusy(true); setCouponError('');
    try {
      const { data } = await api.post('/coupons/validate', {
        code,
        subtotal: Number(subtotal || 0),
        delivery_fee: orderType === 'delivery' ? 3.99 : 0,
      });
      setCoupon(data);
      toast.success(`Coupon ${data.code} applied`);
    } catch (e) {
      setCoupon(null);
      setCouponError(e?.response?.data?.detail || 'Invalid coupon');
    } finally { setCouponBusy(false); }
  };
  const clearCoupon = () => { setCoupon(null); setCouponCode(''); setCouponError(''); };

  const tax = useMemo(() => +(subtotal * taxRate).toFixed(2), [subtotal, taxRate]);
  const deliveryFee = orderType === 'delivery' ? (coupon?.free_delivery_applied ? 0 : 3.99) : 0;
  const discount = coupon && !coupon.free_delivery_applied ? Number(coupon.discount || 0) : 0;
  const total = +(Math.max(0, subtotal - discount) + tax + deliveryFee + Number(tip || 0)).toFixed(2);

  /**
   * When the customer picks a saved address (or switches to "new address"),
   * sync the `address` form state. Selecting a saved entry pre-fills every
   * field so the customer can tweak (e.g. add gate code) without re-typing.
   */
  const handleAddressPick = (id) => {
    setSelectedAddressId(id);
    if (id === '__new__') {
      setAddress({ line1: '', line2: '', city: '', state: '', postal_code: '', notes: '' });
      return;
    }
    const a = savedAddresses.find((x) => x.id === id);
    if (a) {
      setAddress({
        line1: a.line1 || '',
        line2: a.line2 || '',
        city: a.city || '',
        state: a.state || '',
        postal_code: a.postal_code || '',
        notes: a.notes || '',
      });
      // No point saving an existing address again.
      setSaveAddressToAccount(false);
    }
  };

  const placeOrder = async () => {
    if (!items.length) { toast.error('Your cart is empty'); return; }
    if (orderType === 'delivery' && (!address.line1 || !address.city)) { toast.error('Please enter a delivery address'); return; }
    if (orderType === 'dine_in' && !tableId && !tableQrToken) { toast.error('Please select a table'); return; }
    // Both contact fields are mandatory so the kitchen/cashier can reach the
    // customer if anything goes wrong (delivery handover, missing item, SMS
    // pickup notification). We require >=7 digits in the phone to filter out
    // obvious junk like "555" or "x". Applies to authed customers AND guests.
    if (!customerName.trim()) { toast.error('Please enter your name'); return; }
    const phoneDigits = (customerPhone || '').replace(/\D/g, '');
    if (phoneDigits.length < 7) { toast.error('Please enter a valid phone number'); return; }
    setSubmitting(true);
    try {
      const payload = {
        branch_id: branch?.id,
        order_type: orderType,
        items: items.map((i) => ({ menu_item_id: i.menu_item_id, quantity: i.quantity, special_instructions: i.special_instructions })),
        payment_method: paymentMethod,
        customer_name: customerName || undefined,
        customer_phone: customerPhone || undefined,
        tip: Number(tip || 0),
        coupon_code: coupon?.code || undefined,
        special_instructions: undefined,
      };
      if (orderType === 'delivery') payload.delivery_address = address;
      if (orderType === 'dine_in') {
        if (tableId) payload.table_id = tableId;
        if (tableQrToken) payload.table_qr_token = tableQrToken;
      }
      const { data: order } = await api.post('/orders', payload);
      // Optionally persist a brand-new delivery address to the customer's
      // saved-address list AFTER the order is placed (so a network blip on the
      // save call won't block the order itself). Only fires when the customer
      // chose "new address" AND opted in via the checkbox.
      if (
        user
        && orderType === 'delivery'
        && selectedAddressId === '__new__'
        && saveAddressToAccount
        && address.line1
        && address.city
      ) {
        try {
          const { data: savedAddr } = await api.post('/me/addresses', {
            label: (newAddressLabel || 'Home').trim() || 'Home',
            line1: address.line1,
            line2: address.line2 || '',
            city: address.city,
            state: address.state || '',
            postal_code: address.postal_code || '',
            notes: address.notes || '',
          });
          // Cache it locally so the picker shows the new entry if the
          // customer navigates back without a full page refresh.
          setSavedAddresses((prev) => [...prev, savedAddr]);
          toast.success(`Address saved as "${savedAddr.label || 'Home'}"`);
        } catch (_e) {
          // Non-fatal — the order itself succeeded.
        }
      }
      clear();
      if (paymentMethod === 'card') {
        // Initiate Stripe checkout
        const origin = window.location.origin;
        const { data: cs } = await api.post('/payments/checkout/session', { order_id: order.id, origin_url: origin });
        window.location.href = cs.checkout_url;
        return;
      }
      if (paymentMethod === 'mtn_money' || paymentMethod === 'orange_money') {
        try {
          await api.post(`/payments/mobile-money/initiate/${order.id}`, null, { params: { provider: paymentMethod } });
        } catch (e) { /* noop */ }
      }
      toast.success(`Order placed! #${order.order_number}`);
      nav(`/order/${order.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to place order');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
        {cancelled && resumeOrderId && (
          <Card className="mb-4 border-amber-200 bg-amber-50">
            <CardContent className="p-4 text-sm text-amber-900">Payment was cancelled. Your order is still pending. Try again or pick a different payment method.</CardContent>
          </Card>
        )}
        <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
          <div className="space-y-4">
            <Card>
              <CardHeader><CardTitle>Fulfillment</CardTitle></CardHeader>
              <CardContent>
                <RadioGroup value={orderType} onValueChange={setOrderType} className="grid grid-cols-3 gap-3">
                  <FulfillOption value="delivery" label="Delivery" icon={<Truck className="h-4 w-4" />} />
                  <FulfillOption value="pickup" label="Pickup" icon={<ShoppingBag className="h-4 w-4" />} />
                  <FulfillOption value="dine_in" label="Dine-in" icon={<UtensilsCrossed className="h-4 w-4" />} />
                </RadioGroup>

                {orderType === 'delivery' && (
                  <div className="mt-4 grid gap-4">
                    {savedAddresses.length > 0 && (
                      <div className="space-y-3" data-testid="checkout-saved-address-picker">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-medium">Where should we deliver?</Label>
                          {selectedAddressId !== '__new__' && (
                            <button
                              type="button"
                              data-testid="checkout-use-new-address-button"
                              onClick={() => handleAddressPick('__new__')}
                              className="inline-flex items-center gap-1 text-xs font-medium text-primary transition hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded"
                            >
                              <Plus className="h-3 w-3" /> Use a new address
                            </button>
                          )}
                        </div>

                        {savedAddresses.length >= 2 && selectedAddressId !== '__new__' && (
                          <Select value={selectedAddressId} onValueChange={handleAddressPick}>
                            <SelectTrigger data-testid="checkout-saved-address-select" className="h-11">
                              <SelectValue placeholder="Choose a saved address" />
                            </SelectTrigger>
                            <SelectContent>
                              {savedAddresses.map((a) => (
                                <SelectItem
                                  key={a.id}
                                  value={a.id}
                                  data-testid={`checkout-saved-address-select-option-${a.id}`}
                                >
                                  <div className="flex items-center gap-2">
                                    {a.label?.toLowerCase() === 'work'
                                      ? <Briefcase className="h-4 w-4 text-muted-foreground" />
                                      : <Home className="h-4 w-4 text-muted-foreground" />}
                                    <span className="font-medium">{a.label || 'Saved'}</span>
                                    <span className="text-muted-foreground">— {a.line1}{a.city ? `, ${a.city}` : ''}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}

                        {selectedAddressId !== '__new__' && (() => {
                          const a = savedAddresses.find((x) => x.id === selectedAddressId);
                          if (!a) return null;
                          return (
                            <div
                              data-testid={`checkout-saved-address-preview-${a.id}`}
                              className="flex items-start gap-3 rounded-lg border-2 border-primary/40 bg-primary/5 p-4 transition"
                            >
                              <div className="mt-0.5 rounded-full bg-primary/10 p-2 text-primary">
                                {a.label?.toLowerCase() === 'work'
                                  ? <Briefcase className="h-4 w-4" />
                                  : <Home className="h-4 w-4" />}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="text-sm font-semibold text-foreground" data-testid="checkout-selected-address-label">{a.label || 'Saved address'}</p>
                                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">Delivering here</span>
                                </div>
                                <p className="mt-0.5 truncate text-sm text-foreground">{a.line1}{a.line2 ? `, ${a.line2}` : ''}</p>
                                <p className="truncate text-xs text-muted-foreground">{[a.city, a.state, a.postal_code].filter(Boolean).join(', ')}</p>
                                {a.notes && <p className="mt-1 truncate text-xs italic text-muted-foreground">&ldquo;{a.notes}&rdquo;</p>}
                              </div>
                              {savedAddresses.length === 1 && (
                                <button
                                  type="button"
                                  data-testid="checkout-single-address-change-button"
                                  onClick={() => handleAddressPick('__new__')}
                                  className="inline-flex h-8 items-center gap-1 rounded-md border border-border bg-background px-2 text-xs font-medium text-muted-foreground transition hover:border-primary/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                                >
                                  <Pencil className="h-3 w-3" /> Change
                                </button>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                    {(savedAddresses.length === 0 || selectedAddressId === '__new__') && (
                      <div className="grid gap-3 rounded-lg border border-dashed border-border bg-muted/30 p-3 sm:grid-cols-2">
                        {savedAddresses.length > 0 && (
                          <div className="sm:col-span-2 -mb-1 flex items-center justify-between">
                            <p className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                              <MapPinned className="h-3.5 w-3.5" /> Enter a new delivery address
                            </p>
                            <button
                              type="button"
                              data-testid="checkout-cancel-new-address-button"
                              onClick={() => handleAddressPick(savedAddresses[0].id)}
                              className="text-xs font-medium text-muted-foreground transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded"
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                        <Field label="Street address" required><Input data-testid="checkout-address-line1-input" required aria-required="true" value={address.line1} onChange={(e) => setAddress({ ...address, line1: e.target.value })} /></Field>
                        <Field label="Apt / Suite"><Input data-testid="checkout-address-line2-input" value={address.line2} onChange={(e) => setAddress({ ...address, line2: e.target.value })} /></Field>
                        <Field label="City" required><Input data-testid="checkout-address-city-input" required aria-required="true" value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} /></Field>
                        <Field label="Postal code"><Input data-testid="checkout-address-postal-input" value={address.postal_code} onChange={(e) => setAddress({ ...address, postal_code: e.target.value })} /></Field>
                        <div className="sm:col-span-2"><Field label="Delivery notes (gate code, building entry, dropoff instructions)"><Textarea data-testid="checkout-address-notes-input" value={address.notes} onChange={(e) => setAddress({ ...address, notes: e.target.value })} placeholder="Optional — extras the driver needs to find you" /></Field></div>
                        {user && (
                          <div className="sm:col-span-2 flex flex-wrap items-center gap-3 rounded-md bg-background p-2.5">
                            <label htmlFor="save-addr-check" className="flex cursor-pointer items-center gap-2 text-sm">
                              <Checkbox
                                id="save-addr-check"
                                data-testid="checkout-save-address-checkbox"
                                checked={saveAddressToAccount}
                                onCheckedChange={(v) => setSaveAddressToAccount(!!v)}
                              />
                              Save this address to my account
                            </label>
                            {saveAddressToAccount && (
                              <div className="flex items-center gap-2">
                                <Label htmlFor="addr-label-input" className="text-xs text-muted-foreground">Label</Label>
                                <Input
                                  id="addr-label-input"
                                  data-testid="checkout-new-address-label-input"
                                  value={newAddressLabel}
                                  onChange={(e) => setNewAddressLabel(e.target.value)}
                                  className="h-8 w-28 text-sm"
                                  placeholder="Home"
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {orderType === 'dine_in' && (
                  <div className="mt-4 grid gap-3">
                    {tableQrToken ? (
                      <p className="text-sm text-muted-foreground"><MapPin className="mr-1 inline h-4 w-4" /> Linked to your scanned table.</p>
                    ) : (
                      <Field label="Choose table">
                        <select data-testid="checkout-table-select" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={tableId} onChange={(e) => setTableId(e.target.value)}>
                          <option value="">Select table…</option>
                          {tables.map((t) => (
                            <option key={t.id} value={t.id}>Table {t.table_number} ({t.seats} seats)</option>
                          ))}
                        </select>
                      </Field>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Contact</CardTitle></CardHeader>
              <CardContent className="grid gap-3 sm:grid-cols-2">
                <Field label="Name" required><Input data-testid="checkout-name-input" required aria-required="true" value={customerName} onChange={(e) => setCustomerName(e.target.value)} /></Field>
                <Field label="Phone (for SMS updates)" required><Input data-testid="checkout-phone-input" required aria-required="true" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} placeholder="+1 555 ..." /></Field>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Payment</CardTitle></CardHeader>
              <CardContent>
                <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="grid gap-2 sm:grid-cols-2">
                  <PayOption value="cash" label="Cash on arrival" icon={<Wallet className="h-4 w-4" />} desc="Pay in person" />
                  <PayOption value="card" label="Card (Stripe)" icon={<CreditCard className="h-4 w-4" />} desc="Visa, Mastercard, Amex" />
                  <PayOption value="mtn_money" label="MTN Mobile Money" icon={<Smartphone className="h-4 w-4" />} desc="Placeholder integration" />
                  <PayOption value="orange_money" label="Orange Money" icon={<Smartphone className="h-4 w-4" />} desc="Placeholder integration" />
                </RadioGroup>
                <p data-testid="secure-checkout-label" className="mt-3 inline-flex items-center gap-2 rounded-md bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-900">
                  <ShieldCheck className="h-3.5 w-3.5" /> Secure checkout
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Tip the kitchen</CardTitle></CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  <Button data-testid="checkout-tip-0-button" type="button" variant={Number(tip) === 0 ? 'default' : 'secondary'} onClick={() => setTip(0)}>No tip</Button>
                  {policy.tip_suggestions_enabled && Array.isArray(policy.tip_suggestions) && policy.tip_suggestions.map((pct) => {
                    const amount = +(subtotal * (pct / 100)).toFixed(2);
                    const isActive = Number(tip).toFixed(2) === amount.toFixed(2) && tip > 0;
                    return (
                      <Button
                        key={pct}
                        data-testid={`checkout-tip-pct-${pct}-button`}
                        type="button"
                        variant={isActive ? 'default' : 'secondary'}
                        onClick={() => setTip(amount)}
                      >
                        {pct}% · {fmtMoney(amount)}
                      </Button>
                    );
                  })}
                  <Input data-testid="checkout-tip-custom-input" type="number" min={0} step={0.5} value={tip} onChange={(e) => setTip(e.target.value)} className="w-24" />
                </div>
              </CardContent>
            </Card>
          </div>

          <aside>
            <Card className="sticky top-24">
              <CardHeader><CardTitle>Order summary</CardTitle></CardHeader>
              <CardContent>
                {!items.length && <p className="text-sm text-muted-foreground">Your cart is empty</p>}
                <ul className="flex flex-col gap-2">
                  {items.map((i) => (
                    <li key={i.menu_item_id} className="flex items-center justify-between gap-2 text-sm">
                      <span className="truncate">{i.quantity}× {i.name}</span>
                      <span className="tabular">{fmtMoney(i.price * i.quantity)}</span>
                    </li>
                  ))}
                </ul>
                <Separator className="my-3" />
                <div data-testid="checkout-price-breakdown" className="space-y-1 text-sm">
                  <Row label="Subtotal" value={fmtMoney(subtotal)} />
                  {discount > 0 && <Row label={`Discount (${coupon?.code})`} value={`- ${fmtMoney(discount)}`} muted />}
                  {policy.tax_enabled && taxRate > 0 && (
                    <Row label={`Tax (${(taxRate * 100).toFixed(2).replace(/\.00$/, '')}%)`} value={fmtMoney(tax)} />
                  )}
                  {orderType === 'delivery' && (
                    coupon?.free_delivery_applied
                      ? <Row label="Delivery fee" value={<><span className="line-through opacity-50 mr-1">{fmtMoney(3.99)}</span> Free</>} />
                      : <Row label="Delivery fee" value={fmtMoney(deliveryFee)} />
                  )}
                  {Number(tip || 0) > 0 && <Row label="Tip" value={fmtMoney(Number(tip || 0))} />}
                  <Separator className="my-2" />
                  <Row label="Total" value={fmtMoney(total)} bold />
                </div>
                {/* Coupon code input */}
                <div data-testid="checkout-coupon-block" className="rounded-md border border-dashed border-border p-2 text-xs">
                  {!coupon ? (
                    <div className="flex items-center gap-2">
                      <Input
                        data-testid="checkout-coupon-input"
                        placeholder="Promo / coupon code"
                        value={couponCode}
                        onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(''); }}
                        className="h-8 flex-1 font-mono"
                        onKeyDown={(e) => { if (e.key === 'Enter') applyCoupon(); }}
                      />
                      <Button data-testid="checkout-coupon-apply" size="sm" variant="secondary" disabled={!couponCode || couponBusy} onClick={applyCoupon}>Apply</Button>
                    </div>
                  ) : (
                    <div data-testid="checkout-coupon-applied" className="flex items-center justify-between gap-2">
                      <p className="text-emerald-700">
                        <strong className="font-mono">{coupon.code}</strong> applied
                        {coupon.type === 'percent' && ` (${coupon.value}% off)`}
                        {coupon.type === 'fixed' && ` (${fmtMoney(coupon.value)} off)`}
                        {coupon.type === 'free_delivery' && ' (free delivery)'}
                      </p>
                      <Button data-testid="checkout-coupon-remove" size="sm" variant="ghost" onClick={clearCoupon}>Remove</Button>
                    </div>
                  )}
                  {couponError && <p data-testid="checkout-coupon-error" className="mt-1 text-rose-600">{couponError}</p>}
                </div>
                <Button data-testid="checkout-place-order-button" className="mt-4 w-full h-11" onClick={placeOrder} disabled={submitting || !items.length}>
                  {submitting ? 'Placing order…' : `Place order · ${fmtMoney(total)}`}
                </Button>
                <p className="mt-2 text-center text-[11px] text-muted-foreground">By placing this order you agree to our terms.</p>
              </CardContent>
            </Card>
          </aside>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <div className="space-y-1.5">
      <Label>
        {label}
        {required && (
          <span
            data-testid={`checkout-field-required-${String(label).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
            aria-hidden="true"
            className="ml-0.5 text-rose-600"
          >
            *
          </span>
        )}
      </Label>
      {children}
    </div>
  );
}
function Row({ label, value, bold }) {
  return (
    <div className={`flex items-center justify-between ${bold ? 'text-base font-semibold' : 'text-sm'}`}>
      <span>{label}</span><span className="tabular">{value}</span>
    </div>
  );
}
function FulfillOption({ value, label, icon }) {
  return (
    <label htmlFor={`ful-${value}`} data-testid={`checkout-fulfillment-${value}`} className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card p-3 text-sm hover:bg-secondary peer-checked:border-primary">
      <RadioGroupItem id={`ful-${value}`} value={value} />
      {icon}
      <span>{label}</span>
    </label>
  );
}
function PayOption({ value, label, icon, desc }) {
  return (
    <label htmlFor={`pay-${value}`} data-testid={`checkout-payment-${value}`} className="flex cursor-pointer items-start gap-3 rounded-lg border border-border bg-card p-3 hover:bg-secondary">
      <RadioGroupItem id={`pay-${value}`} value={value} className="mt-1" />
      <div>
        <div className="flex items-center gap-2 text-sm font-medium">{icon} {label}</div>
        <div className="text-xs text-muted-foreground">{desc}</div>
      </div>
    </label>
  );
}
